import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../Hooks';
import { Button, Checkbox, Groupper, Header, Icon, Input, Message, Modal, Placeholder, Stat, Table, Toast } from 'react-frontier';
import { addCommas, bindClose } from '@payfront/components/Util';
import { SetLoading } from '../DashboardClasses';
import Validator from '@payfront/components/Validator';
import API from '../API';
import UserAccess from '../UserAccess';
import { firstBy } from 'thenby';

interface UserData{
	user_id: number,
	first_name: string,
	last_name: string,
	email: string,
	admin: boolean,
	access: number[]
}

var UserList = ()=>{
	var { t } = useTranslation();
	var { active_client, hasClientAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [users, setUsers] = useState<UserData[]>(null);
	var [selectUser, setSelectUser] = useState<boolean>(false);
	var [searchEmail, setSearchEmail] = useState<string>('');
	var [shownUser, setShownUser] = useState<UserData>(null);
	var [userAccess, setUserAccess] = useState<number[]>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		if(active_client==-1) return;
		API.getUserList(active_client).then(res=>{
			if(res.error) return setLoadError(res.message);
			setUsers(res.data);
		}).catch(err=>{
			return setLoadError(t('common.unexpected_error', { code: 'LCL-USRLST-1' }));
		});
	}, []);

	useEffect(()=>{
		setModalPrompts(null);
	}, [selectUser, shownUser]);
	
	var canEdit = useMemo(()=>{
		return hasClientAccess(UserAccess.USERS.EDIT_USER_ACCESS);
	}, [active_client]);

	var saveUser = (setLoading: SetLoading)=>{
		if(!canEdit || !shownUser) return;
		if(userAccess.length==0) return setModalPrompts([t('users.empty_access')]);
		setModalPrompts(null);

		setLoading(true);
		API.setUserAccess(active_client, shownUser.user_id, userAccess).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setUsers([
				...users.filter(a=>a.user_id!==shownUser.user_id),
				{
					...shownUser,
					access: userAccess,
				}
			].sort(firstBy('user_id', 'asc')));
			setShownUser(null);
			Toast.success(t('users.user_saved'));
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_error', { code: 'LCL-SVUSRAC-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var searchUser = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({ email: searchEmail }, {
			email: [{
				rule: 'email', prompt: t('users.invalid_email')
			}]
		});

		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.searchUser(searchEmail.trim().toLowerCase()).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			if(users.find(a=>a.user_id===res.data.user_id)){
				return setModalPrompts([t('users.already_user')])
			}

			setSelectUser(false);
			setShownUser({
				user_id: res.data.user_id,
				first_name: res.data.first_name,
				last_name: res.data.last_name,
				email: res.data.email,
				admin: false,
				access: [],
			});
			setUserAccess([]);
		}).catch(err=>{
			setModalPrompts([t('common.unexpected_error', { code: 'LCL-USRSCH-1' })]);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var viewUser = (user: UserData)=>{
		return ()=>{
			if(!canEdit) return Toast.error(t('common.no_access'))
			setUserAccess(user.access || []);
			setShownUser(user);
		}
	}

	var changeUserAccess = (access_id: number)=>{
		return (val: boolean)=>{
			if(!canEdit) return Toast.error(t('common.no_access'))
			if(val){
				setUserAccess([...(userAccess || []), access_id]);
			}else{
				setUserAccess(userAccess ? userAccess.filter(a=>a!==access_id) : []);
			}
		}
	}

	if(loadError) return <Header text={t('common.error')} subtext={loadError} />
	
	return <div className='pf payout-list-screen user-list-screen' style={{ padding: 15 }}>
		<Stat.Group>
			<Stat value={(
				<Placeholder height={36} value={addCommas(users?.length, false)} />
			)} label={t('common.users')}></Stat>
		</Stat.Group>
		<Groupper title={t('common.users')} titleRight={(
			<Button text={t('common.add')} color='black' iconName='plus-circle' onClick={bindClose(setSelectUser, true)} />
		)}>
			<Table striped fitted
				className='last'
				collapsingIndexes={[0, 2]}
				headers={['ID', t('common.name'), t('common.email'), t('common.accesses')]}
			>
				{!users ? <>
					<Table.Row collapsingIndexes={[0, 2, 3]} data={[ <Placeholder width={10} />, <Placeholder />, <Placeholder width={100} />, <Placeholder width={5} />, ]} />
					<Table.Row collapsingIndexes={[0, 2, 3]} data={[ <Placeholder width={10} />, <Placeholder />, <Placeholder width={100} />, <Placeholder width={5} />, ]} />
				</> : users.length==0 ? (
					<Table.Cell empty row colSpan={4}>
						{t('users.no_users')}
					</Table.Cell>
				) : users.map(a=>(
					<Table.Row
						selectable={canEdit}
						collapsingIndexes={[0, 2, 3]}
						centeredIndexes={[3]}
						onClick={canEdit ? viewUser(a) : null}
						key={`USRL-${a.user_id}`}
						data={[
							a.user_id,
							`${a.first_name} ${a.last_name}`,
							a.email,
							a.admin ? (
								<i style={{ color: 'gray' }}>Administrador</i>
							) : a.access.length
						]}
					/>
				))}
			</Table>
		</Groupper>
		
		<Modal open={selectUser} onClose={bindClose(setSelectUser)} size='tiny'>
			<Modal.Header>{t('users.add_user')}</Modal.Header>
			<Modal.Content>
				<Input label='Correo electrónico' value={searchEmail} onChange={setSearchEmail} />
				<Message>
					{t('users.add_user_message')}
				</Message>
				<Message type='error' list={modalPrompts} style={{ marginTop: 15 }} />
			</Modal.Content>
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClose(setShownUser)} />
				<Button color='black' text='Continuar' onClick={searchUser} />
			</Modal.Actions>
		</Modal>
		<Modal open={!!shownUser} onClose={bindClose(setShownUser)} size='tiny'>
			<Modal.Header>{t(shownUser?.access.length>0 ? 'users.edit_user' : 'users.add_user')}</Modal.Header>
			{!!shownUser && (
				<Modal.Content>
					<Input label='Correo electrónico' value={shownUser.email} readonly />
					{shownUser.admin ? (
						<Groupper title={t('common.access')}>
							<Header text={t('users.user_admin')} subtext={t('users.cant_edit_access')} />
						</Groupper>
					) : (
						<Table title={t('common.access')} collapsingIndexes={[0]}>
							{UserAccess.ACCESS_ARRAY.map(c=><>
								<Table.Row header>
									<Table.Cell colSpan={2}>{c.name}</Table.Cell>
								</Table.Row>
								{c.access.map(a=>(
									<Table.Row
										collapsingIndexes={[0]}
										data={[
											<Checkbox checked={userAccess ? userAccess?.indexOf(a.id)!=-1 : false} onChange={changeUserAccess(a.id)} label={'.'} labelStyle={{ color: 'transparent', width: 0, marginLeft: 0 }} style={{ marginLeft: 10 }} />,
											a.name
										]}
									/>
								))}
							</>)}
						</Table>
					)}
					<Message type='error' list={modalPrompts} style={{ marginTop: 10 }} />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClose(setShownUser)} />
				{!shownUser?.admin && (
					<Button color='black' text='Guardar' onClick={saveUser} />
				)}
			</Modal.Actions>
		</Modal>
	</div>
}

export default UserList;